import { Link, useNavigate } from "react-router-dom";
import { Drawer, Badge, Dropdown, MenuProps, Menu, notification } from "antd";
import { BellOutlined, SettingOutlined, HomeOutlined } from '@ant-design/icons';
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { startTransition, useEffect, useState, useRef } from "react";
import { Button, Flex, Input, Modal, Image } from "antd";
import { useStore } from "../../../stores/root-store-context";
import LanguageSwitcher from "../../elements/LanguageSwitcher";

interface ERPHeaderProps {
    isOffCanvas: boolean;
    handleOffCanvas: () => void;
    isSearch: boolean;
    handleSearch: () => void;
}

const ERPHeader = observer(({
    isOffCanvas,
    handleOffCanvas,
    isSearch,
    handleSearch,
}: ERPHeaderProps) => {
    const { applicationStore } = useStore();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [visibleModal, setVisibleModal] = useState<string>("");
    const [modalData, setModalData] = useState({ oldData: "", newData: "" });
    const [notificationDrawerVisible, setNotificationDrawerVisible] = useState<boolean>(false);
    const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);

    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const handleModalOk = async () => {
        setVisibleModal("");
        setModalData({ oldData: "", newData: "" });
    };

    const items: MenuProps["items"] = [
        {
            onClick: () => {
                startTransition(() => {
                    navigate(`/${applicationStore.company?.Tenant}/employees/${applicationStore.user.id}`);
                });
            },
            label: <span style={{ padding: "0px 16px" }}>{t("Menu.MyAccount")}</span>,
            key: "1",
        },
        {
            onClick: () => {
                startTransition(() => {
                    setModalData({ oldData: "", newData: "" });
                    setVisibleModal("email");
                });
            },
            label: <span style={{ padding: "0px 16px" }}>{t("ChangeEmail")}</span>,
            key: "Email",
        },
        {
            onClick: () => {
                startTransition(() => {
                    setModalData({ oldData: "", newData: "" });
                    setVisibleModal("password");
                });
            },
            label: <span style={{ padding: "0px 16px" }}>{t("ChangePassword")}</span>,
            key: "Password",
        },
        {
            onClick: () => {
                startTransition(() => {
                    applicationStore.logout();
                    navigate('/');
                });
            },
            label: <span style={{ padding: "0px 16px" }}>{t("LogOut")}</span>,
            key: "LogOut",
        },
    ];

    const notificationsEndRef = useRef<HTMLDivElement>(null);

    const showNotificationDrawer = () => {
        setNotificationDrawerVisible(true);
    };

    const closeNotificationDrawer = () => {
        if (applicationStore.notifications) {
            applicationStore.notifications = applicationStore.notifications.map((notification: any) => {
                if (!notification.isRead) {
                    applicationStore.markNotificationAsRead(parseInt(notification.key));
                    return { ...notification, isRead: true };
                }
                return notification;
            });
        }
        setNotificationDrawerVisible(false);
    };

    const shouldShowHamburger = windowWidth <= 1330;

    return (
        <>
            <header>
                <Modal cancelText={`${t("Cancel")}`} onOk={handleModalOk} open={visibleModal !== ""} onCancel={() => setVisibleModal("")}>
                    {visibleModal === "email" && (
                        <div>
                            <div>{"\u200B"}</div>
                            <Input placeholder="new email" value={modalData.newData} onChange={
                                (e) => setModalData({ ...modalData, newData: e.target.value })
                            } />
                        </div>
                    )}
                    {visibleModal === "password" && (
                        <Flex style={{ lineHeight: "1.6", flexDirection: "column" }}>
                            <div>{"\u200B"}</div>
                            <Input.Password placeholder="old password" value={modalData.oldData} onChange={
                                (e) => setModalData({ ...modalData, oldData: e.target.value })
                            } />
                            <div>{"\u200f"}</div>
                            <Input.Password placeholder="new password" type="password" value={modalData.newData} onChange={
                                (e) => setModalData({ ...modalData, newData: e.target.value })
                            } />
                        </Flex>
                    )}
                </Modal>
                <div id="header-sticky" className="header-1">
                    <div className="container-fluid">
                        <div className="mega-menu-wrapper">
                            <div className="header-main style-2">
                                <div className="header-left">
                                    <div className="logo">
                                        <Link to={`/${applicationStore.company?.Tenant}`} className="header-logo">
                                            {applicationStore.company?.LogoId ?
                                                <img src={`${applicationStore?.siteConfig?.baseCloudinary}/image/upload/${applicationStore.company?.LogoId}`} alt="logo-img" height={60} />
                                                :
                                                <img src="/assets/logo_black.png" alt="logo-img" height={60} />
                                            }
                                        </Link>
                                    </div>
                                </div>

                                                                    <Button 
                                        type="link"
                                        icon={<HomeOutlined />}
                                        onClick={() => navigate(`/${applicationStore.company?.Tenant}`)}
                                        style={{ marginLeft: '10px', position: "absolute", left: "47.5%" }}
                                    >
                                        {window.location.pathname.includes('/erp') ? t('ExitERP') : t('ExitCRM')}
                                    </Button>
                                <div className="header-right d-flex justify-content-end align-items-center">
                                    {applicationStore?.siteConfig?.isShowLanguage && <LanguageSwitcher />}
                                    {applicationStore.isAuthenticated &&
                                        <>
                                            <a onClick={showNotificationDrawer} style={{ cursor: 'pointer' }}>
                                                <Badge count={applicationStore.notifications?.filter((notification: any) => !notification.isRead).length || 0}>
                                                    <BellOutlined style={{ fontSize: 20 }} />
                                                </Badge>
                                            </a>
                                            <Drawer
                                                title={t("Notifications")}
                                                placement="right"
                                                onClose={closeNotificationDrawer}
                                                open={notificationDrawerVisible}
                                            >
                                                {applicationStore.notifications && applicationStore.notifications.length > 0 ? (
                                                    <ul style={{ padding: 0, listStyleType: 'none' }}>
                                                        {applicationStore.notifications.map((notification: any, index: any) => {
                                                            if (notification && 'label' in notification && 'onClick' in notification) {
                                                                const label = notification.translationKey
                                                                    ? t(notification.translationKey, notification.translationParams)
                                                                    : notification.label;

                                                                return (
                                                                    <li
                                                                        key={index}
                                                                        onClick={() => {
                                                                            if (!notification.isRead) {
                                                                                applicationStore.markNotificationAsRead(parseInt(notification.key));
                                                                                notification.isRead = true;
                                                                                if (applicationStore.notifications) {
                                                                                    applicationStore.notifications = [...applicationStore.notifications];
                                                                                }
                                                                            }
                                                                            const tenant = applicationStore.company?.Tenant;
                                                                            const link = `/${tenant}${notification.link}`;
                                                                            window.location.href = link;
                                                                        }}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            padding: '10px 15px',
                                                                            borderBottom: '1px solid #f0f0f0',
                                                                            display: 'flex',
                                                                            justifyContent: 'space-between',
                                                                            alignItems: 'center',
                                                                            backgroundColor: notification.isRead ? '#ffffff' : '#ffffff',
                                                                            color: notification.isRead ? '#888888' : '#000000'
                                                                        }}
                                                                    >
                                                                        <span style={{ marginRight: '10px', flex: 1 }}>{label}</span>
                                                                        <span style={{ color: '#888', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                                            {notification.date}
                                                                        </span>
                                                                    </li>
                                                                );
                                                            }
                                                            return null;
                                                        })}
                                                        <div ref={notificationsEndRef} />
                                                    </ul>
                                                ) : (
                                                    <p>No notifications available</p>
                                                )}
                                            </Drawer>

                                            <Dropdown
                                                trigger={["click"]}
                                                placement="bottomLeft"
                                                arrow={{ pointAtCenter: true }}
                                                menu={{ items: items }}
                                            >
                                                <Image
                                                    preview={false}
                                                    width={50}
                                                    height={50}
                                                    style={{ borderRadius: "50%", objectFit: "cover", minWidth: 50 }}
                                                    src={applicationStore.user?.photoId ? `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/w_300/${applicationStore.user?.photoId}` :
                                                        `https://res.cloudinary.com/dnl3x07wo/image/upload/w_300/z03w7hlsy8hcoic9dlcn`
                                                    }
                                                />
                                            </Dropdown>
                                        </>
                                    }
                                    <div className="header__hamburger my-auto" style={{ display: shouldShowHamburger ? 'block' : 'none' }}>
                                        <div className="sidebar__toggle" onClick={handleOffCanvas}>
                                            <i className="fas fa-bars" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
});

export default ERPHeader;